import { Flash, TickCircle, Warning2 } from "iconsax-reactjs";
import type { useTranslations } from "next-intl";
import type React from "react";
import { toEffectiveBv } from "@/hooks/user/useReferralPercent";
import type { WeekStatus } from "@/types/api/user.types";

type AlertColors = "default" | "error" | "alert" | "success" | "link";

type WeekStatusT = ReturnType<typeof useTranslations<"dashboard.weekStatus">>;

/**
 * Build a status → label map from the `dashboard.weekStatus` translator.
 * Module scope cannot call `useTranslations`, so the consuming component
 * passes `t` via `getStatusText(t)[status]`.
 */
export const getStatusText = (
  t: WeekStatusT,
): Record<WeekStatus | "default", string> => ({
  CALCULATED: t("statusCalculated"),
  WAITING_FOR_BV: t("emptyStatus"),
  INSUFFICIENT_BV: t("statusNotCalculated"),
  CAN_CALCULATE: t("statusCanCalculate"),
  default: t("statusUnknown"),
});

/**
 * Map status to Alert color variant
 */
export function getStatusColor(status: WeekStatus): AlertColors {
  switch (status) {
    case "CALCULATED":
      return "success";
    case "WAITING_FOR_BV":
      return "alert";
    case "INSUFFICIENT_BV":
      return "error";
    case "CAN_CALCULATE":
      return "success";
    default:
      return "default";
  }
}

/**
 * Get icon component for status
 */
export function getStatusIcon(status: WeekStatus): React.ReactNode {
  switch (status) {
    case "CALCULATED":
      return <TickCircle variant="Bold" className="text-semantic-success" />;
    case "WAITING_FOR_BV":
      return <Warning2 variant="Bold" className="text-semantic-alert" />;
    case "INSUFFICIENT_BV":
      return <Warning2 variant="Bold" className="text-semantic-error" />;
    case "CAN_CALCULATE":
      return <Flash variant="Bold" className="text-semantic-success" />;
    default:
      return <Warning2 variant="Bold" className="text-text-primary" />;
  }
}

export function safeParseJsonB(value: string) {
  if (typeof value !== "string") return value;

  // If it's already valid JSON
  try {
    return JSON.parse(value);
  } catch {}

  // Try to fix unquoted keys like {bv: 66, sv: 66}
  try {
    const fixed = value
      .replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":') // quote keys
      .replace(/'/g, '"'); // replace single quotes with double
    return JSON.parse(fixed);
  } catch {
    console.warn("Invalid JSONB format:", value);
    return null;
  }
}

/**
 * Extract BV value from a child node structure
 * @param child - Child node object with nested struct.bv structure
 * @param side - Which side to extract: "left" or "right"
 * @returns BV value as number, or 0 if not found
 */
/**
 * A leg's displayable total: settled BV plus pending BV with the referral cut
 * applied.
 *
 * The two halves arrive in different units. `struct.bv.left` mirrors the chain,
 * which credits legs with EFFECTIVE bv; `struct.bv.pending_left` is the GROSS
 * cart bv, because that is all the backend stores and it cannot derive the cut
 * (that needs `referralPercent` from the chain, and the backend makes no RPC
 * calls). The backend used to add them raw, which overstated every pending
 * order by the referral percentage — a 2 UVM purchase showed as 2 in the
 * upline's genealogy, then dropped to 1.82 once the weekly calculate ran.
 *
 * Pending is added to both the direct leg and the `all_*` total, matching what
 * the backend did before the split.
 */
export function getLegBv(
  child: unknown,
  side: "left" | "right" | "all_left" | "all_right",
  referralPercentWei: bigint,
): number {
  const settled = getBVValue(child, side);
  const pendingSide = side.endsWith("left") ? "pending_left" : "pending_right";
  const pendingGross = getBVValue(child, pendingSide as never);

  return settled + toEffectiveBv(pendingGross, referralPercentWei);
}

export function getBVValue(
  child: unknown,
  side: "left" | "right" | "all_left" | "all_right",
): number {
  if (
    child &&
    typeof child === "object" &&
    "struct" in child &&
    child.struct &&
    typeof child.struct === "object" &&
    "bv" in child.struct &&
    child.struct.bv &&
    typeof child.struct.bv === "object"
  ) {
    const bv = child.struct.bv as Record<string, unknown>;
    if (side in bv && typeof bv[side] === "number") {
      return bv[side] as number;
    }
  }
  return 0;
}
